import {useEffect, useRef, useState} from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {ChatPreview} from 'shells/chat/chat-preview';
import {
    CHAT_ACTION,
    BOOKING_ACTION,
    EMAIL_ACTION,
    CALL_ACTION,
    TEXT_ACTION,
    QUOTE_ACTION,
} from 'shells/chat/consts';
import {colors} from 'shells/colors/colors';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {PhoneOutlineIcon} from 'shells/icon/svg/phone-outline-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {PageContentPreviewable} from 'shells/layout/page-content-previewable/page-content-previewable';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {PromoBanner} from 'shells/promo-banner/promo-banner';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';

import {ChatSettingsIntakeFormFieldsEnum} from 'nutshell-graphql-types';
import type {ChatSettingsIntakeFormTypeEnum} from 'nutshell-graphql-types';
import type {
    ChatSettingsActionsInput,
    ChatSettingsIntakeFormInput,
    ChatSettingsLookAndFeelInput,
} from 'nutshell-graphql-types';

import {useDismissibleUserSetting} from '../../../../../util/hooks/use-dismissible-user-setting';

import {useGetChatSettingsEngagementBar} from '../../graphql/hooks/use-get-chat-settings-engagement-bar';
import {useGetWfFormsChat} from '../../graphql/hooks/use-get-wf-forms-chat';
import {useUpdateChatSettings} from '../../graphql/hooks/use-update-chat-settings';

import {getChatWidgetIcon} from '../helpers';
import {SettingsContentWrapper} from '../settings-content-wrapper';

import {BookingCard} from './booking-card';
import {CallCard} from './call-card';
import {ChatCard} from './chat-card';
import {SAVE_DELAY, SAVE_STATE_KEYS} from './consts';
import type {SaveStateKey, WidgetType} from './consts';
import {EmailCard} from './email-card';
import {Header} from './header';
import {QuoteCard} from './quote-card';
import {TextCard} from './text-card';

export function ActionsOptions() {
    const [openWidget, setOpenWidget] = useState<WidgetType | null | undefined>(null);
    const [isDisclaimerEnabled, setIsDisclaimerEnabled] = useState<boolean>(false);
    const [disclaimerMessage, setDisclaimerMessage] = useState<string>('');
    const [allowThreadsWithoutContact, setAllowThreadsWithoutContact] = useState<boolean>(false);
    const [intakeFormType, setIntakeFormType] =
        useState<ChatSettingsIntakeFormTypeEnum>('CONVERSATIONAL');
    const [intakeFormFields, setIntakeFormFields] = useState<
        Array<ChatSettingsIntakeFormFieldsEnum>
    >([ChatSettingsIntakeFormFieldsEnum.name]);

    const {shouldShow: showEngagementBanner, onDismiss: dismissEngagementBanner} =
        useDismissibleUserSetting(Nut_Settings_User.SETTING_HAS_DISMISSED_ENGAGEMENT_REPORT_BANNER);

    const handleDismissEngagementBanner = () => {
        if (dismissEngagementBanner) {
            dismissEngagementBanner();
        }
    };

    const {
        chatSettings,
        isLoading: isLoadingChatSettings,
        refetch: refetchSettings,
    } = useGetChatSettingsEngagementBar();

    const {updateChatSettings} = useUpdateChatSettings();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const {toastState, saveFailed} = useManageSaveState(isSaving, SAVE_DELAY);
    const [savingToastKey, setSavingToastKey] = useState<SaveStateKey | null | undefined>(null);

    // Refs for each widget card to enable auto-scrolling
    const widgetRefs = useRef<Partial<Record<WidgetType, HTMLDivElement | null | undefined>>>({});

    // Auto-scroll to opened widget
    useEffect(() => {
        if (!openWidget) {
            return;
        }

        const targetElement = widgetRefs.current[openWidget];
        if (targetElement) {
            // Give the widget enough time to expand before scrolling
            setTimeout(() => {
                targetElement.scrollIntoView({behavior: 'smooth', block: 'start'});
            }, 100);
        }
    }, [openWidget]);

    useEffect(() => {
        if (chatSettings) {
            setIsDisclaimerEnabled(chatSettings.lookAndFeel.disclaimerSettings.isEnabled);
            setDisclaimerMessage(chatSettings.lookAndFeel.disclaimerSettings.message);
            setAllowThreadsWithoutContact(chatSettings.intakeForm.allowThreadsWithoutContact);
            setIntakeFormType(chatSettings.intakeForm.type);
            setIntakeFormFields(chatSettings.intakeForm.fields);
        }
    }, [chatSettings]);

    const handleActionSave = (updates: ChatSettingsActionsInput, toastKey: SaveStateKey) => {
        setIsSaving(true);
        setSavingToastKey(toastKey);

        return updateChatSettings({
            chatSettings: {
                actions: updates,
            },
        })
            .then(() => {
                refetchSettings();
                setIsSaving(false);
                setTimeout(() => {
                    setSavingToastKey(null);
                }, 1000);
            })
            .catch((e) => {
                // Reset saving state
                setIsSaving(false);

                // This is needed to reset toastState to 'initial'
                saveFailed();

                // Save state is managed further down, need to rethrow
                // So it sees this error
                throw e;
            });
    };

    const handleIntakeFormSave = (updates: ChatSettingsIntakeFormInput, toastKey: SaveStateKey) => {
        setIsSaving(true);
        setSavingToastKey(toastKey);

        return updateChatSettings({
            chatSettings: {
                intakeForm: updates,
            },
        }).then(() => {
            refetchSettings();
            setIsSaving(false);
            setTimeout(() => {
                setSavingToastKey(null);
            }, 1000);
        });
    };

    const handleLookAndFeelSave = (
        updates: ChatSettingsLookAndFeelInput,
        toastKey: SaveStateKey
    ) => {
        setIsSaving(true);
        setSavingToastKey(toastKey);

        return updateChatSettings({
            chatSettings: {
                lookAndFeel: updates,
            },
        }).then(() => {
            refetchSettings();
            setIsSaving(false);
            setTimeout(() => {
                setSavingToastKey(null);
            }, 1000);
        });
    };

    // Debounced save for disclaimer
    const disclaimerSaveTimeoutRef = useRef<number | null | undefined>(null);

    // Helper to decouple timeout management from save functionality
    // Makes it so we can still return a Promise from handleDisclaimerSave
    const createDebouncedSave = (saveTimeoutRef: {current: number | null | undefined}) => {
        return (saveFunction: () => Promise<any>, delay: number): Promise<any> => {
            return new Promise((res, rej) => {
                if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                }

                // @ts-expect-error
                saveTimeoutRef.current = setTimeout(() => {
                    // Execute save and forward Promise result
                    saveFunction().then(res).catch(rej);
                }, delay);
            });
        };
    };

    const debouncedSave = createDebouncedSave(disclaimerSaveTimeoutRef);

    // Needs to return a Promise so we can use it in useDebouncedSaveField further down
    const handleDisclaimerSave = (htmlValue: string): Promise<any> => {
        setDisclaimerMessage(htmlValue);

        return debouncedSave(
            () =>
                handleLookAndFeelSave(
                    {
                        disclaimerSettings: {
                            isEnabled: isDisclaimerEnabled,
                            message: htmlValue,
                        },
                    },
                    SAVE_STATE_KEYS.DISCLAIMER
                ),
            SAVE_DELAY
        );
    };

    const {forms, isLoading: isLoadingForms} = useGetWfFormsChat({
        limit: 100,
        after: null,
        archived: false,
        sort: 'NAME_ASC',
    });

    const getLeftContent = () => {
        return (
            <div className='flex flex-dir-col mb-32 pr-8'>
                <Stack spacing={32}>
                    <Header />
                    <Stack spacing={16}>
                        {isLoadingChatSettings || !chatSettings || !chatSettings.actions ? (
                            <>
                                <LoadingText isFullWidth={true} height={47} />
                                <LoadingText isFullWidth={true} height={47} />
                                <LoadingText isFullWidth={true} height={47} />
                                <LoadingText isFullWidth={true} height={47} />
                                <LoadingText isFullWidth={true} height={47} />
                                <LoadingText isFullWidth={true} height={47} />
                            </>
                        ) : (
                            <>
                                <ChatCard
                                    cardRef={(el) => {
                                        widgetRefs.current.chat = el;
                                    }}
                                    actionSettings={chatSettings.actions.chat}
                                    isLoadingChatSettings={isLoadingChatSettings}
                                    openWidget={openWidget}
                                    setOpenWidget={setOpenWidget}
                                    isDisclaimerEnabled={isDisclaimerEnabled}
                                    setIsDisclaimerEnabled={setIsDisclaimerEnabled}
                                    disclaimerMessage={disclaimerMessage}
                                    handleLookAndFeelSave={handleLookAndFeelSave}
                                    handleDisclaimerSave={handleDisclaimerSave}
                                    allowThreadsWithoutContact={allowThreadsWithoutContact}
                                    setAllowThreadsWithoutContact={setAllowThreadsWithoutContact}
                                    intakeFormType={intakeFormType}
                                    setIntakeFormType={setIntakeFormType}
                                    intakeFormFields={intakeFormFields}
                                    setIntakeFormFields={setIntakeFormFields}
                                    openingMessage={chatSettings.lookAndFeel.conversationStarter}
                                    toastState={toastState}
                                    savingToastKey={savingToastKey}
                                    handleActionSave={handleActionSave}
                                    handleIntakeFormSave={handleIntakeFormSave}
                                />
                                <BookingCard
                                    cardRef={(el) => {
                                        widgetRefs.current.book = el;
                                    }}
                                    openWidget={openWidget}
                                    setOpenWidget={setOpenWidget}
                                    actionSettings={chatSettings.actions.booking}
                                    toastState={toastState}
                                    savingToastKey={savingToastKey}
                                    handleSave={handleActionSave}
                                />
                                <EmailCard
                                    cardRef={(el) => {
                                        widgetRefs.current.email = el;
                                    }}
                                    actionSettings={chatSettings.actions.email}
                                    forms={forms}
                                    isLoadingForms={isLoadingForms}
                                    openWidget={openWidget}
                                    setOpenWidget={setOpenWidget}
                                    toastState={toastState}
                                    savingToastKey={savingToastKey}
                                    handleSave={handleActionSave}
                                />
                                <CallCard
                                    cardRef={(el) => {
                                        widgetRefs.current.call = el;
                                    }}
                                    actionSettings={chatSettings.actions.call}
                                    forms={forms}
                                    isLoadingForms={isLoadingForms}
                                    openWidget={openWidget}
                                    setOpenWidget={setOpenWidget}
                                    toastState={toastState}
                                    savingToastKey={savingToastKey}
                                    handleSave={handleActionSave}
                                />
                                <TextCard
                                    cardRef={(el) => {
                                        widgetRefs.current.text = el;
                                    }}
                                    actionSettings={chatSettings.actions.text}
                                    forms={forms}
                                    isLoadingForms={isLoadingForms}
                                    openWidget={openWidget}
                                    setOpenWidget={setOpenWidget}
                                    toastState={toastState}
                                    savingToastKey={savingToastKey}
                                    handleSave={handleActionSave}
                                />
                                <QuoteCard
                                    cardRef={(el) => {
                                        widgetRefs.current.quote = el;
                                    }}
                                    actionSettings={chatSettings.actions.quote}
                                    forms={forms}
                                    isLoadingForms={isLoadingForms}
                                    openWidget={openWidget}
                                    setOpenWidget={setOpenWidget}
                                    toastState={toastState}
                                    savingToastKey={savingToastKey}
                                    handleSave={handleActionSave}
                                />
                            </>
                        )}
                    </Stack>
                </Stack>
                {showEngagementBanner &&
                    dismissEngagementBanner &&
                    NutClientConfig.hasEngagementReportFF && (
                        <div className='mt-16'>
                            <PromoBanner
                                illustration={<Illustration src='engagement-report' />}
                                title='Engagement report'
                                description='See insights on actions, leads, and sales in one place.'
                                ctaText='Go to Engagement report'
                                ctaHref='/reports/engagement'
                                onDismiss={handleDismissEngagementBanner}
                            />
                        </div>
                    )}
            </div>
        );
    };

    const getPreview = () => {
        if (!chatSettings) {
            return (
                <div className='flex justify-center align-center full-width full-height'>
                    <LoadingText height={500} width={305} color={colors.plumLt} />
                </div>
            );
        }

        switch (openWidget) {
            case 'book':
                return (
                    <ChatPreview
                        headerTitle='Meet with:'
                        subheader='Sales Representative'
                        status='booking'
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        shouldHideInputField={true}
                        showSubmitButton={true}
                        submitButtonText='Choose a date'
                        actions={chatSettings.actions}
                        actionBarEnabled={true}
                        actionBarShownActions={[BOOKING_ACTION]}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                    />
                );
            case 'email':
                return (
                    <ChatPreview
                        headerTitle={chatSettings.lookAndFeel.nameDisplay}
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        status='engagement-form'
                        formType='Email'
                        formDescription='Your Nutshell email form goes here'
                        formIcon={<EnvelopeNewIcon size={12} wrapWithDiv={false} />}
                        shouldHideInputField={true}
                        showSubmitButton={true}
                        submitButtonText='Send email request'
                        shouldLinkItem={chatSettings.actions.email.shouldLinkEmailAddress}
                        linkItem={chatSettings.actions.email.linkEmailAddress}
                        actions={chatSettings.actions}
                        actionBarEnabled={true}
                        actionBarShownActions={[EMAIL_ACTION]}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                    />
                );
            case 'call':
                return (
                    <ChatPreview
                        headerTitle={chatSettings.lookAndFeel.nameDisplay}
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        status='engagement-form'
                        formType='Call'
                        formDescription='Your Nutshell call form goes here'
                        formIcon={<PhoneOutlineIcon size={12} wrapWithDiv={false} />}
                        shouldHideInputField={true}
                        showSubmitButton={true}
                        submitButtonText='Send call request'
                        shouldLinkItem={chatSettings.actions.call.shouldLinkNumber}
                        linkItem={chatSettings.actions.call.linkNumberConfig.customNumber}
                        actions={chatSettings.actions}
                        actionBarEnabled={true}
                        actionBarShownActions={[CALL_ACTION]}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                    />
                );
            case 'text':
                return (
                    <ChatPreview
                        headerTitle={chatSettings.lookAndFeel.nameDisplay}
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        status='engagement-form'
                        formType='Text'
                        formDescription='Your Nutshell text form goes here'
                        formIcon={<PhoneOutlineIcon size={12} wrapWithDiv={false} />}
                        shouldHideInputField={true}
                        showSubmitButton={true}
                        submitButtonText='Send text request'
                        shouldLinkItem={chatSettings.actions.call.shouldLinkNumber}
                        linkItem={chatSettings.actions.call.linkNumberConfig.customNumber}
                        actions={chatSettings.actions}
                        actionBarEnabled={true}
                        actionBarShownActions={[TEXT_ACTION]}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                    />
                );
            case 'quote':
                return (
                    <ChatPreview
                        headerTitle={chatSettings.lookAndFeel.nameDisplay}
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        status='engagement-form'
                        formType='Quote'
                        formDescription='Your Nutshell quote form goes here'
                        shouldHideInputField={true}
                        showSubmitButton={true}
                        submitButtonText='Send quote request'
                        actions={chatSettings.actions}
                        actionBarEnabled={true}
                        actionBarShownActions={[QUOTE_ACTION]}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                    />
                );
            case 'chat':
            default:
                return (
                    <ChatPreview
                        headerTitle={chatSettings.lookAndFeel.nameDisplay}
                        icon={getChatWidgetIcon(
                            chatSettings.lookAndFeel.icon.type,
                            chatSettings.lookAndFeel.icon.url
                        )}
                        backgroundColor={chatSettings.lookAndFeel.color}
                        secondaryColor={chatSettings.lookAndFeel.secondaryColor}
                        status='online'
                        conversationStarter={chatSettings.lookAndFeel.conversationStarter}
                        actions={chatSettings.actions}
                        actionBarEnabled={true}
                        actionBarShownActions={[CHAT_ACTION]}
                        shouldSelectAction={false}
                        showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                        showDisclaimer={isDisclaimerEnabled}
                        disclaimerText={disclaimerMessage}
                        intakeFormSettings={{
                            allowThreadsWithoutContact,
                            type: intakeFormType,
                            fields: intakeFormFields,
                        }}
                    />
                );
        }
    };

    return (
        <SettingsContentWrapper>
            <PageContentPreviewable getPreview={getPreview} leftContent={getLeftContent()} />
        </SettingsContentWrapper>
    );
}
